import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Copy,
  Loader2,
  Shield,
  ShieldOff,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { supabase } from "@/lib/supabase";

interface VerifiedFactor {
  id: string;
  friendly_name: string | null;
  status: "verified" | "unverified";
  factor_type: string;
}

interface EnrollState {
  factor_id: string;
  qr_svg: string;
  secret: string;
  uri: string;
}

/**
 * Two-factor (TOTP) controls for the dashboard Settings page.
 *
 * Lets a signed-in user enroll an authenticator app (Google Auth,
 * 1Password, Authy, etc.) and disable it later. Sign-in enforcement
 * lives in DashboardLayout: if the user has a verified factor but the
 * current session is still aal1, a blocking challenge screen renders
 * before any dashboard content.
 *
 * Requires Supabase Auth → Multi-Factor Authentication enabled at the
 * project level. If the API rejects enrol with a "MFA disabled"
 * message, that's the toggle to flip in the dashboard.
 */
export function TwoFactorSection() {
  const [factor, setFactor] = useState<VerifiedFactor | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<EnrollState | null>(null);
  const [code, setCode] = useState("");
  const [working, setWorking] = useState(false);

  async function loadFactors() {
    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      // 401 / not signed in is the only realistic case here. Keep
      // quiet; the rest of the dashboard will redirect.
      setLoading(false);
      return;
    }
    const verifiedTotp = (data?.totp ?? []).find(
      (f: { status: string }) => f.status === "verified",
    );
    setFactor(verifiedTotp ? (verifiedTotp as VerifiedFactor) : null);
    setLoading(false);
  }

  useEffect(() => {
    loadFactors();
  }, []);

  async function startEnroll() {
    setWorking(true);
    // Clean up any half-finished unverified TOTP factors so the
    // friendlyName uniqueness check doesn't 422 us.
    const { data: existing } = await supabase.auth.mfa.listFactors();
    for (const f of (existing?.totp ?? []) as Array<{ id: string; status: string }>) {
      if (f.status !== "verified") {
        await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {});
      }
    }

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: `Whistll dashboard · ${new Date().toLocaleDateString()}`,
    });
    setWorking(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setEnrolling({
      factor_id: data.id,
      qr_svg: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    });
  }

  async function verifyEnroll() {
    if (!enrolling) return;
    if (!/^[0-9]{6}$/.test(code)) {
      return toast.error("Enter the 6-digit code from your authenticator.");
    }
    setWorking(true);
    const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
      factorId: enrolling.factor_id,
    });
    if (chalErr) {
      setWorking(false);
      return toast.error(chalErr.message);
    }
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: enrolling.factor_id,
      challengeId: chal.id,
      code,
    });
    setWorking(false);
    if (verErr) {
      return toast.error(verErr.message);
    }
    toast.success("Two-factor enabled.");
    setEnrolling(null);
    setCode("");
    await loadFactors();
  }

  async function cancelEnroll() {
    if (!enrolling) return;
    await supabase.auth.mfa
      .unenroll({ factorId: enrolling.factor_id })
      .catch(() => {});
    setEnrolling(null);
    setCode("");
  }

  async function disable() {
    if (!factor) return;
    if (
      !window.confirm(
        "Disable two-factor? Your account is more vulnerable without it. You can re-enable any time.",
      )
    ) {
      return;
    }
    setWorking(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
    setWorking(false);
    if (error) return toast.error(error.message);
    toast.success("Two-factor disabled.");
    await loadFactors();
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-1 flex items-center gap-2">
        <Shield className="h-4 w-4 text-volt" />
        <h2 className="text-sm font-bold uppercase tracking-wider">
          Two-factor authentication
        </h2>
      </div>
      <p className="mb-5 text-sm text-muted-foreground">
        Add an extra step at sign-in using an authenticator app (Google
        Authenticator, 1Password, Authy). Strongly recommended for
        admin accounts.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking your factors…
        </div>
      ) : factor ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-volt/30 bg-volt-subtle p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-volt" />
            <div className="text-sm">
              <p className="font-semibold">Two-factor is on</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {factor.friendly_name ?? "Authenticator"} ·{" "}
                <span className="font-mono">{factor.id.slice(0, 8)}…</span>
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="destructive"
            onClick={disable}
            disabled={working}
          >
            {working ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Disabling…
              </>
            ) : (
              <>
                <ShieldOff className="h-3 w-3" />
                Disable
              </>
            )}
          </Button>
        </div>
      ) : (
        <Button onClick={startEnroll} disabled={working}>
          {working ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Setting up…
            </>
          ) : (
            <>
              <Shield className="h-3 w-3" />
              Enable two-factor
            </>
          )}
        </Button>
      )}

      {enrolling && (
        <EnrollDialog
          state={enrolling}
          code={code}
          onChangeCode={setCode}
          onVerify={verifyEnroll}
          onCancel={cancelEnroll}
          working={working}
        />
      )}
    </section>
  );
}

function EnrollDialog({
  state,
  code,
  onChangeCode,
  onVerify,
  onCancel,
  working,
}: {
  state: EnrollState;
  code: string;
  onChangeCode: (v: string) => void;
  onVerify: () => void;
  onCancel: () => void;
  working: boolean;
}) {
  async function copySecret() {
    try {
      await navigator.clipboard.writeText(state.secret);
      toast.success("Secret copied - paste it into your authenticator if QR doesn't scan.");
    } catch {
      toast.error("Couldn't copy. Long-press the value to select it.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Set up two-factor</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Scan the QR with your authenticator app, then enter the
              6-digit code it shows you.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Supabase returns the QR as inline SVG markup. */}
        <div
          className="mx-auto w-fit rounded-lg bg-white p-3"
          dangerouslySetInnerHTML={{ __html: state.qr_svg }}
        />

        <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">
              Manual key
            </span>
            <button
              type="button"
              onClick={copySecret}
              className="inline-flex items-center gap-1 text-xs font-semibold hover:text-volt"
            >
              <Copy className="h-3 w-3" />
              Copy
            </button>
          </div>
          <code className="mt-1 block break-all font-mono text-[11px] text-foreground">
            {state.secret}
          </code>
        </div>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">6-digit code</span>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => onChangeCode(e.target.value.replace(/\D/g, ""))}
            disabled={working}
            className="font-mono tracking-[0.4em]"
          />
        </label>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={onVerify} disabled={working || code.length !== 6}>
            {working ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Verifying…
              </>
            ) : (
              <>Verify &amp; enable</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
